import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import axiosInstance from '../../../../../axios/axiosInstance';
import apiEndpoints from '../../../../../axios/api';
import { usePageRefresh } from '../../../../../context/PageRefreshContext';
import { TenancyStatusBadge, formatDate } from './tenancyStatus';

/**
 * Every tenant a flat has had, newest tenancy first, with start / end dates and
 * an Active / Inactive action per row.
 */
const TenantHistoryModal = ({ flatId, refreshToken, onClose, onToggleStatus }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        if (!flatId) return;
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(apiEndpoints.GET_TENANT_HISTORY_API, { params: { flat_id: flatId } });
            setData(res?.data || null);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load tenant history');
        } finally {
            setLoading(false);
        }
    }, [flatId]);

    // Reload when opened for another flat, and after a status change
    // (refreshToken is bumped by the page).
    useEffect(() => { load(); }, [load, refreshToken]);
    usePageRefresh(load);

    if (!flatId) return null;

    const tenants = data?.tenants || [];
    const unit = data?.flat ? [data.flat.wing_title, data.flat.flat_number].filter(Boolean).join(' / ') : '';

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-3xl tw-shadow-xl tw-max-h-[85vh] tw-flex tw-flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:history" className="tw-text-xl tw-text-indigo-600" />
                        <div>
                            <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">Tenant History</h3>
                            {unit && <p className="tw-text-[11px] tw-text-gray-500">Flat {unit}</p>}
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                        <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                    </button>
                </div>

                <div className="tw-p-5 tw-overflow-y-auto">
                    {loading && (
                        <div className="tw-flex tw-justify-center tw-py-8">
                            <div className="tw-animate-spin tw-rounded-full tw-h-7 tw-w-7 tw-border-b-2 tw-border-indigo-600"></div>
                        </div>
                    )}
                    {!loading && error && (
                        <p className="tw-text-sm tw-text-rose-700 tw-bg-rose-50 tw-border tw-border-rose-100 tw-rounded-lg tw-p-3">{error}</p>
                    )}
                    {!loading && !error && tenants.length === 0 && (
                        <p className="tw-text-center tw-text-sm tw-text-gray-500 tw-py-8">No tenants recorded for this flat.</p>
                    )}
                    {!loading && !error && tenants.length > 0 && (
                        <div className="tw-overflow-x-auto tw-border tw-border-gray-100 tw-rounded-lg">
                            <table className="tw-w-full tw-text-xs">
                                <thead className="tw-bg-gray-50">
                                    <tr>
                                        <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">Tenant</th>
                                        <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">Contact</th>
                                        <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">Start Date</th>
                                        <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">End Date</th>
                                        <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-500">Status</th>
                                        {onToggleStatus && <th className="tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-gray-500">Action</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenants.map((t) => {
                                        const ended = t.tenancy_status === 'INACTIVE';
                                        // The status modal reads flat/wing for its summary line.
                                        const row = { ...t, flat: { flat_number: data.flat.flat_number, wing: { title: data.flat.wing_title } } };
                                        return (
                                            <tr key={t.occupant_id} className="tw-border-t tw-border-gray-100">
                                                <td className="tw-px-3 tw-py-2 tw-font-medium tw-text-gray-800">
                                                    {[t.first_name, t.last_name].filter(Boolean).join(' ')}
                                                </td>
                                                <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                                    <p>{t.phone || '-'}</p>
                                                    <p className="tw-text-[10px] tw-text-gray-400">{t.email || ''}</p>
                                                </td>
                                                <td className="tw-px-3 tw-py-2 tw-text-gray-600">{formatDate(t.start_date)}</td>
                                                <td className="tw-px-3 tw-py-2 tw-text-gray-600">{formatDate(t.end_date)}</td>
                                                <td className="tw-px-3 tw-py-2"><TenancyStatusBadge member={t} /></td>
                                                {onToggleStatus && (
                                                    <td className="tw-px-3 tw-py-2 tw-text-center">
                                                        <button
                                                            onClick={() => onToggleStatus(row)}
                                                            className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-border ${ended
                                                                ? 'tw-border-emerald-200 tw-text-emerald-700 hover:tw-bg-emerald-50'
                                                                : 'tw-border-amber-200 tw-text-amber-700 hover:tw-bg-amber-50'}`}
                                                        >
                                                            {ended ? 'Mark Active' : 'Mark Inactive'}
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-text-end">
                    <button onClick={onClose} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TenantHistoryModal;
